import { colors } from '@chegg-ui/theme'
import {
  BLURRED_CARD_KEY,
  CUSTOM,
  GRAMMAR,
  NEW,
  PLAGIARISM,
  READ,
} from '@src/components/Cards/constants'
import { CardProps } from '@src/components/Cards/types'
import { TokenKeyProps } from '@src/components/Editor/Highlight'
import {
  CardContent,
  CardContentWithoutEK,
  Comment,
  ModuleContent,
} from '@src/components/Editor/types'
import { IssueCategory } from '@src/components/Modals/PaperScorecardModal/types'
import { RawEditorEntity } from '@src/mocks/editorState'
import { getPaidMember } from '@src/state/auth/selectors'
import { CheckState } from '@src/state/checks/reducer'
import { getCommentsFromRawEntity } from '@src/state/document/helpers/draftjs.helper'
import { RootState } from '@src/state/types'
import {
  CheckMetadata,
  CheckType,
  GrammarReplacement,
  PlagiarismReplacement,
  Replacement,
} from '@src/types/checks'
import { insertUuidHyphens } from '@src/utils/analytics'
import { getWordCount } from '@src/utils/wordCount'
import { ContentState, EditorState } from 'draft-js'
import { createSelector } from 'reselect'
import { getCharacterCount, getFileExtension } from '../analytics/helpers'
import { isDemoSelector } from '../router/selectors'
import {
  ClickstreamSuccessState,
  PaperMetadata,
  SubjectTaxonomy,
} from '../userevents/actions'
import { UndoReplacement } from '../workspace/reducer'
import {
  CHECKER_PRIORITIES,
  getCommentsByCategory,
  getFilename,
  getUploadId,
} from '../workspace/selectors'
import { CommentsMap } from './actions'
import { mapCommentCategoryToIssueCategory } from './helpers'
import { Metadata } from './reducer'
import {
  CheckCategory,
  FilteredComments,
  IssueCategoryCount,
  IssueCategoryCounts,
  TypePriorities,
} from './types'

export const selectAllComments = createSelector<RootState, CommentsMap, CardContent[]>(
  (state: RootState) => state.document.comments,
  commentsMap => Object.values(commentsMap)
)

// TODO: is this still needed?
export const selectInteractableComments = createSelector<
  RootState,
  CardContent[],
  CardContent[]
>(selectAllComments, comments => {
  return comments.filter(
    comment =>
      comment.cardState === NEW ||
      comment.cardState === READ ||
      comment.cardState === 'gated'
  )
})

const MIN_WORD_COUNT: number = 20
export const FREEMIUM_PLAG_WORD_COUNT: number = 500

export const getCategoryFromType = (type: CheckType) =>
  type === 'plagiarism' ? PLAGIARISM : GRAMMAR

export const selectFilters = (state: RootState): string[] => state.workspace.filters

export const getPlainText = (state: RootState): string =>
  state.document.editorState.getCurrentContent().getPlainText()

export const getSubjectTaxonomy = (state: RootState): SubjectTaxonomy => {
  const { metadata, score } = state.document

  const {
    kgSubjectName,
    kgSubjectUuid,
    modelVersion,
    modelVariant,
  } = metadata as Metadata

  const uuid = kgSubjectUuid ? insertUuidHyphens(kgSubjectUuid) : null

  return [
    {
      uuid,
      name: kgSubjectName || '',
      model_classification: kgSubjectName || '',
      content_relationship: 'inferred',
      model_name: 'paper-subject-classifier',
      model_version: modelVersion || '',
      model_variant: modelVariant || '',
      model_score: score || null,
    },
  ]
}

export const getPaperMetadata = (
  state: RootState,
  eventState: ClickstreamSuccessState,
  checkType: string,
  failed?: boolean
): PaperMetadata => {
  const text = getEditorText(state)

  return {
    state: eventState,
    check_type: checkType,
    upload_id: getUploadId(state),
    character_count: getCharacterCount(text),
    word_count: getWordCount(text),
    file_type: getFileExtension(getFilename(state)),
    is_failed: failed,
  }
}

export const getChecks = (state: RootState) => {
  const { currentCheckCategory } = state.workspace
  return Object.keys(state.checks).reduce((checkObj, check: CheckType) => {
    if (currentCheckCategory === getCategoryFromType(check)) {
      checkObj[check] = state.checks[check]
    }
    return checkObj
  }, {})
}

export const getMaxFreeComments = (state: RootState): number =>
  state.workspace.maxFreeComments

export const hasOverlappingRange = (firstRange: any, comparisonRange: any) => {
  return (
    firstRange.start <= comparisonRange.end && firstRange.end >= comparisonRange.start
  )
}

export const isDuplicate = (
  commentA: CardContent | CardContentWithoutEK,
  commentB: CardContent | CardContentWithoutEK
) => commentA.errorText === commentB.errorText && hasOverlappingRange(commentA, commentB)

export const getEditorState = (state: RootState) => state.document.editorState

export const getContentState = createSelector<RootState, EditorState, ContentState>(
  [getEditorState],
  editorState => editorState.getCurrentContent()
)

export const isFocusedKey = (state: RootState, props: CardProps) => {
  if (state.workspace.focusedComponent === 'editor') {
    return (
      state.workspace.selectedEntityId === props.id ||
      state.workspace.selectedEntityId === props.parentId
    )
  }
  return state.workspace.selectedEntityId === props.id
}

// TODO: what is this for?
export const isEntityModified = (state: RootState, props: CardProps) => {
  const content = state.document.editorState.getCurrentContent()

  if (props.entityKeys && content) {
    try {
      const data = content.getEntity(props.entityKeys[0]).getData()
      const updatedErrorText = content
        .getFirstBlock()
        .getText()
        .substring(props.start, props.end)

      return data.errorText !== updatedErrorText
    } catch {
      // try/catch to handle case where entity no longer exists
      return false
    }
  }
  return false
}

export const IsEnclosedByEntity = (start: number, end: number, entity: any) =>
  (entity && start >= entity.start && start <= entity.end) ||
  (entity && end >= entity.start && end <= entity.end)

const getSelectedEntityKey = (state: RootState) => state.workspace.selectedEntityId

export const getEntityFromKey = (state: RootState, props: TokenKeyProps) =>
  state.document.editorState.getCurrentContent().getEntity(props.entityKey)

// UI handles background highlighting
export const selectKeyForFocused = createSelector(
  [getEntityFromKey, getSelectedEntityKey],
  (entity, selectedEntityId) => {
    const e: RawEditorEntity = entity.getData()
    const ids = Object.keys(e.comments)

    return ids.some((id: string) => {
      return id === selectedEntityId
    })
  }
)

export const selectHighlightColor = createSelector(
  [(state: RootState) => state.workspace.currentCheckCategory],
  category => {
    switch (category) {
      case GRAMMAR:
        return colors.sky
      case PLAGIARISM:
        return colors.cheggOrange
      default:
        return colors.white
    }
  }
)

export const selectEntityType = createSelector(
  [(state: RootState) => state.workspace.currentCheckCategory],
  category => category
)

export const isGatedEntity = createSelector(
  [getEntityFromKey],
  entity => entity.getData().cardState === 'gated'
)

// UI underline selector
export const selectIsFiltered = createSelector(
  [(state: RootState) => state.workspace.currentCheckCategory, getEntityFromKey],
  (checkCategory, entity) => {
    const comments = getCommentsFromRawEntity(entity)
    return comments.some(category => category === checkCategory)
  }
)

export const getModules = (state: RootState) => state.modules

export const mapMetadataToComment = (
  metadata: CheckMetadata,
  modules: ModuleContent[]
): CardContentWithoutEK => {
  const moduleId = getModuleId(metadata)

  const module = modules.find(current => current.id === moduleId) as ModuleContent

  const {
    analytics,
    id,
    offset,
    message,
    length,
    state,
    errorText,
    type,
    rule,
    replacements,
    category,
    glimpseId,
    submissionId,
  } = metadata

  return {
    id,
    documentId: '1',
    start: offset,
    end: offset + length,
    moduleId,
    description: message,
    status: state,
    cardState: state,
    type,
    errorText,
    color: module.color || '#e74c3c',
    moduleType: module.type,
    rule,
    replacements: replacements.map((replacement: Replacement) => {
      if ((replacement as PlagiarismReplacement).cite) {
        return replacement as PlagiarismReplacement
      }
      return replacement as GrammarReplacement
    }),
    analytics,
    category,
    submissionId,
    glimpseId,
  }
}

export const getRawComments = (comments: CheckMetadata[]) =>
  createSelector<RootState, ModuleContent[], CardContentWithoutEK[]>(
    [getModules],
    modules => comments.map(comment => mapMetadataToComment(comment, modules))
  )

export const getCommentCountFromState = createSelector<RootState, CheckState, number>(
  [getChecks],
  checks =>
    Object.keys(checks).reduce<number>(
      (count: number, check: CheckType) => count + (checks[check].count || 0),
      0
    )
)

export const getOrderedComments = (metadata: CheckMetadata[]) =>
  createSelector<
    RootState,
    CardContentWithoutEK[],
    CardContent[],
    Array<CardContentWithoutEK | CardContent>
  >(
    [state => getRawComments(metadata)(state), selectInteractableComments],
    (rawComments, commentsFromState) => {
      const mixedComments = [...rawComments, ...commentsFromState]
      return mixedComments.sort((a: CardContent, b: CardContent) => a.start - b.start)
    }
  )

export const getFilteredComments = (metadata: CheckMetadata[]) =>
  createSelector(
    [(state: RootState) => getOrderedComments(metadata)(state)],
    (comments: CardContent[]) => filterDuplicates(comments, CHECKER_PRIORITIES)
  )

export const filterDuplicates = (
  comments: CardContent[],
  priorities: TypePriorities
): FilteredComments => {
  let prevComment: CardContent
  return comments.reduce(
    (result: FilteredComments, comment: CardContent, index) => {
      // automatically add to shown[] on first index
      if (index === 0) {
        result.shown = [comment]
        return result
      }
      // comments sorted, so dups will be in adjacent indices so we can check
      // against last index of shown[]
      prevComment = result.shown.slice(-1)[0]
      if (isDuplicate(prevComment, comment)) {
        // check which dup to show based on type priority
        if (priorities[prevComment.type] < priorities[comment.type]) {
          // replace previous comment with new comment, add previous comment to hidden
          result.shown = [...result.shown.slice(0, -1), comment]
          result.hidden = [...result.hidden, prevComment]
        } else {
          // add new comment to hidden
          result.hidden = [...result.hidden, comment]
        }
      } else {
        // if isn't a dup, append to shown array
        result.shown = [...result.shown, comment]
      }
      return result
    },
    { shown: [], hidden: [] }
  )
}
// Aligns issue type with moduleId
export const getModuleId = (comment: CheckMetadata) => {
  switch (comment.rule) {
    case 'wtai-tii':
    case 'wtai-plagscan':
    case 'plagiarism':
      return 1
    case 'UnknownToken':
      return 4
    case 'typographical':
      return 3
    case 'style':
    case 'inconsistency':
      return 5
    // Grammar by default
    default:
      return 2
  }
}

export const selectEntitledComments = createSelector(
  (state: RootState) => state.workspace.currentCheckCategory,
  selectInteractableComments,
  (category: CheckCategory, comments): CardContent[] => {
    const categoryComments = getCommentsByCategory(category, comments)
    const entitledCategoryComments = categoryComments.filter(c => c.cardState !== 'gated')
    return entitledCategoryComments.sort(
      (a: CardContent, b: CardContent) => a.start - b.start
    )
  }
)

export const hasExtraComments = (state: RootState) => state.workspace.hasMaxFreeComments

export const selectGatedComments = createSelector<
  RootState,
  CardContent[],
  CardContent[]
>(selectInteractableComments, comments =>
  comments.filter(comment => comment.cardState === 'gated')
)

export const selectForBlurredCard = createSelector(
  [getPaidMember, isDemoSelector, selectGatedComments],
  (isPaidMember, isDemo, gatedComments) =>
    !isPaidMember && !isDemo && gatedComments.length > 0
)

export const selectCommentsWithCustomEntities = createSelector<
  RootState,
  CardContent[],
  boolean,
  Comment[]
>([selectEntitledComments, selectForBlurredCard], (comments, showBlurredCard) => {
  if (showBlurredCard) {
    return [
      ...comments,
      {
        type: CUSTOM,
        id: BLURRED_CARD_KEY,
      },
    ]
  }

  return comments
})

export const selectCardForFocused = createSelector([isFocusedKey], isFocused => isFocused)

export const selectCardIsModified = createSelector(
  [isEntityModified],
  isModified => isModified
)

export const getEditorText = (state: RootState): string =>
  state.document.editorState.getCurrentContent().getPlainText()

export const selectEditorTextWords = createSelector([getEditorText], (text: string) =>
  text.split(' ')
)

export const selectForMinTextLength = createSelector(
  [selectEditorTextWords],
  (words: string[]) => words.length >= MIN_WORD_COUNT
)

export const selectFreemiumPlagText = createSelector(
  [selectEditorTextWords],
  (words: string[]) => {
    const wordsToCheck = words.slice(0, FREEMIUM_PLAG_WORD_COUNT)
    return wordsToCheck.join(' ')
  }
)

export const selectUndoReplacement = (state: RootState) => state.workspace.undoReplacement
export const getUndoReplacementIndex = (
  undoReplacement: UndoReplacement | undefined,
  comments: Comment[],
  showBlurredCard: boolean
): UndoReplacement | undefined => {
  if (!undoReplacement) {
    return undefined
  }

  const { undoEditorStartIndex } = undoReplacement

  const undoIndex = comments.findIndex(
    (card: CardContent) => undoEditorStartIndex <= card.start
  )

  // find the index where the card was, if upsell/blurred card is there and index not found (because of last card)
  // then put ahead, otherwise undo will be only item
  const undoListIndex =
    undoIndex > -1
      ? undoIndex
      : showBlurredCard
      ? comments.length - 1
      : comments.length || -1

  return { ...undoReplacement, undoListIndex }
}
export const selectUndoReplacementIndex = createSelector(
  [selectUndoReplacement, selectCommentsWithCustomEntities, selectForBlurredCard],
  getUndoReplacementIndex
)

export const selectGrammarCardCtaIndex = (state: RootState) =>
  state.workspace.grammarCardCtaIndex

export const selectDocumentLoading = (state: RootState) => state.document.loading

export const selectInteractableGrammarComments = createSelector<
  RootState,
  CardContent[],
  CardContent[]
>(selectInteractableComments, comment => getCommentsByCategory(GRAMMAR, comment))

export const selectInteractablePlagComments = createSelector<
  RootState,
  CardContent[],
  CardContent[]
>(selectInteractableComments, comment => getCommentsByCategory(PLAGIARISM, comment))

export const selectPlagirismCommentsCount = createSelector<
  RootState,
  CardContent[],
  number
>(selectInteractablePlagComments, comments => comments.length)

export const selectGrammarCommentsCount = createSelector(
  selectInteractableGrammarComments,
  comments => comments.length
)

export const selectWordCount = createSelector([getEditorText], (text: string) =>
  getWordCount(text)
)

export const selectPaperSubject = (state: RootState) => state.document.subject

export const selectPlagiarismSimilarityScore = (state: RootState) => {
  if (state.document.plagiarismScore) {
    return Math.round(state.document.plagiarismScore)
  }
  return undefined
}

export const selectIssueCategoryCounts = createSelector(
  selectInteractableGrammarComments,
  (comments): IssueCategoryCount[] => {
    const issueCategoryCounts = comments
      // Change comments to an object of issue categories and counts { 'issueCategory': count }
      .map(comment => mapCommentCategoryToIssueCategory(comment.category))
      .reduce((acc: IssueCategoryCounts, issueCategory: IssueCategory) => {
        if (issueCategory in acc) {
          acc[issueCategory] && acc[issueCategory]!++
        } else {
          acc[issueCategory] = 1
        }
        return acc
      }, {})

    return Object.keys(issueCategoryCounts)
      .map(key => {
        return {
          issueCategory: key,
          count: issueCategoryCounts[key],
        } as IssueCategoryCount
      })
      .sort((a, b) => b.count - a.count)
  }
)



// WEBPACK FOOTER //
// ./src/state/document/selectors.ts